import type { ItemRow } from './ItemsTable'
import { ParserStatsPanel, type HighlightMode } from './ParserStatsPanel'
import type { ParsedLineStatus } from '../utils/mapIntakeResult'
import { jumpToItemRow } from '../utils/flashAnchor'

// «Результат распознавания» на странице «Новая заявка»: аннотированный вид
// строк (левая колонка) + сводная панель статистики по позициям (правая
// колонка). На узких экранах — одна колонка, панель уходит под список.
// Вынесено из NewRequestPage (≤500 строк), разметка не менялась.
export function ParsedLinesView(props: {
  parsedLines: ParsedLineStatus[]
  /** null — фильтр подсветки выключен, показываем все строки. */
  visibleLineNums: Set<number> | null
  items: ItemRow[]
  highlightMode: HighlightMode
  onHighlight: (mode: HighlightMode, uids: Set<string>) => void
  onClearHighlight: () => void
}) {
  const { visibleLineNums } = props
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-3" style={{ height: '50vh' }}>
      <div className="border border-gray-200 rounded-lg overflow-auto h-full min-h-0">
        {props.parsedLines
          .filter((p) => visibleLineNums == null || visibleLineNums.has(p.lineNum))
          .map(({ line, lineNum, status, warnings, homoglyph }) => (
            <div
              key={lineNum}
              id={`parsed-line-${lineNum}`}
              className={`border-b border-gray-100 last:border-0 px-3 py-1 text-xs font-mono transition-colors ${
                status === 'skipped' ? 'bg-gray-50 text-gray-400' : 'bg-white'
              }`}
            >
              <div className="flex items-start gap-2">
                <span className="shrink-0 w-5 text-center mt-0.5">
                  {status === 'ok' && <span className="text-green-600">✓</span>}
                  {status === 'warn' && <span className="text-red-500">✗</span>}
                  {status === 'skipped' && <span className="text-gray-300">—</span>}
                </span>
                <button
                  type="button"
                  onClick={() => jumpToItemRow(lineNum)}
                  title="Перейти к этой позиции в таблице"
                  className="shrink-0 text-gray-500 hover:text-brand-600 hover:underline w-7 text-right cursor-pointer"
                >
                  ({lineNum})
                </button>
                <span className={`whitespace-pre ${status === 'warn' ? 'text-red-700' : ''}`}>
                  {line}
                </span>
              </div>
              {homoglyph && (
                <div className="ml-[3.25rem] mt-0.5 text-[11px] leading-tight text-red-600">
                  ⚠ кириллица в артикуле:{' '}
                  <span className="font-mono">
                    {homoglyph.segments.map((s, i) => (
                      <span key={i} className={s.bad ? 'rounded bg-red-100 font-bold' : ''}>
                        {s.text}
                      </span>
                    ))}
                  </span>{' '}
                  → латиница «{homoglyph.canonical}»
                </div>
              )}
              {warnings && warnings.length > 0 && (
                <div className="ml-[3.25rem] mt-0.5 text-[11px] leading-tight text-red-600">
                  {warnings.map((w, i) => (
                    <div key={i}>⚠ {w}</div>
                  ))}
                </div>
              )}
            </div>
          ))}
      </div>
      <ParserStatsPanel
        items={props.items}
        highlightMode={props.highlightMode}
        onHighlight={props.onHighlight}
        onClearHighlight={props.onClearHighlight}
      />
    </div>
  )
}
